import graphql__1dbd03b7974fb700a60a from "./__generated__/MapDroppableZone_droppableZone.graphql.js";import { useCallback, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { useSelector } from "react-redux";
import { Polygon, useMap } from "react-leaflet";
import { useDrop } from "react-dnd";
import insidePolygon from "point-in-polygon";

import { MAP_ELEMENT_DRAGGABLE_SOURCE } from "./MapElementDraggableSource";
import getSVGElementBounds from "../Utils/getSVGElementBounds";
import getLatLngFromContainerPoint from "../Utils/getLatLngFromContainerPoint";
import { useMapContext } from "./MapContext";

const MapDroppableZone = ({ zIndexOffset = 0, ...props }) => {
  const droppableZone = useFragment(MapDroppableZone_droppableZone, props.droppableZone);
  const { multiPolygons: droppableZoneMultiPolygons, hasCoordinates } = droppableZone;

  const { mapKey } = useMapContext();

  const { draggedContribution: isContributionDragged, contributionCreation } =
    useSelector(({ map }) => map[mapKey].contribution);
  const displayZone = isContributionDragged || contributionCreation?.allowLocation;

  const { onClickInZone } = props;

  const leafletMap = useMap();

  const [leafletElementPolygons, setLeafletElementPolygons] = useState({});

  // This fetches the SVG <path> element from the leaflet polygon element and stores it
  // in our local state to be used to check whether the user drops a draggable source or
  // clicks inside the droppable zone.
  const setElementPolygon = useCallback((id, leafletElement) => {
    setTimeout(() => {
      const polygon = leafletElement.getElement();

      setLeafletElementPolygons(prevPaths => {
        if (prevPaths[id] === polygon) return prevPaths;

        return { ...prevPaths, [id]: polygon };
      });
    }, 0);
  }, []);

  const getMultiPolygonsCoordinates = useCallback(
    multiPolygons => multiPolygons.edges.map(edge => edge.node.coordinates),
    []
  );

  const isValidPoint = useCallback(
    (point, multiPolygonsCoordinates) =>
      multiPolygonsCoordinates.some(polyCoords =>
        insidePolygon(point, polyCoords.flat().flat())
      ),
    []
  );

  // This handles mobile clicks in the clickable container, and calls the click callback
  // if the click was inside the zone polygon's coordinates.
  const handleClick = useCallback(
    e => {
      if (!onClickInZone) return;

      const { lat, lng } = getLatLngFromContainerPoint(e.clientX, e.clientY, leafletMap);

      const point = [lat, lng];

      const clickValid = isValidPoint(
        point,
        getMultiPolygonsCoordinates(droppableZoneMultiPolygons)
      );

      if (clickValid) {
        onClickInZone({ lat, lng });
      }
    },
    [
      droppableZoneMultiPolygons,
      getMultiPolygonsCoordinates,
      isValidPoint,
      leafletMap,
      onClickInZone
    ]
  );

  // This handles desktop draggable source drops inside the container and returns the
  // coordinates where it was dropped and whether it was dropped inside the zone polygon's
  // coordinates through the `isValid` property
  const drop = useCallback(
    (_, monitor) => {
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return null;
      }

      const { x, y } = clientOffset;
      const { style: draggedMarkerStyle } = monitor.getItem();
      const { lat, lng } = getLatLngFromContainerPoint(
        x,
        y,
        leafletMap,
        draggedMarkerStyle
      );

      const point = [lat, lng];

      const isValid = isValidPoint(
        point,
        getMultiPolygonsCoordinates(droppableZoneMultiPolygons)
      );

      return { x, y, lat, lng, isValid };
    },
    [leafletMap, isValidPoint, getMultiPolygonsCoordinates, droppableZoneMultiPolygons]
  );

  // Connect the react-dnd's context to the droppable zone. As for the `useDrag` hook,
  // the first returned element contains the collected props and the second argument
  // is a callback ref that allows us to connect our container to the react-dnd's event
  // handling system.
  const [{ isOver }, dropRef] = useDrop(() => ({
    accept: MAP_ELEMENT_DRAGGABLE_SOURCE,
    drop,
    collect: monitor => ({ isOver: monitor.isOver() })
  }));

  const containerPosition = useMemo(() => {
    if (!Object.keys(leafletElementPolygons).length) return {};

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    Object.values(leafletElementPolygons).forEach(path => {
      const bounds = getSVGElementBounds(path, leafletMap.getContainer());

      if (bounds) {
        minX = Math.min(minX, bounds.left);
        minY = Math.min(minY, bounds.top);
        maxX = Math.max(maxX, bounds.left + bounds.width);
        maxY = Math.max(maxY, bounds.top + bounds.height);
      }
    });

    return {
      left: `${minX}px`,
      top: `${minY}px`,
      width: `${maxX - minX}px`,
      height: `${maxY - minY}px`
    };
  }, [leafletMap, leafletElementPolygons]);

  // The container is only shown in certain circumstances, most often when an element
  // is already being dragged to highlight the zone to the user and help him understand
  // that he should drop its source item into the container.
  if (!displayZone || !hasCoordinates) return null;

  const containerStyle = {
    position: "absolute",
    ...containerPosition,
    opacity: isOver ? 0.5 : 0
  };

  return (
    <div style={containerStyle} onClick={handleClick} ref={dropRef}>
      {droppableZoneMultiPolygons.edges.map(({ node: polygonData }) => (
        <Polygon
          key={polygonData.id}
          ref={element => element && setElementPolygon(polygonData.id, element)}
          positions={polygonData.coordinates}
          zIndexOffset={zIndexOffset}
          color={polygonData.style?.borderStyle?.color}
          fillColor={polygonData.style?.backgroundStyle?.color}
        />
      ))}
    </div>
  );
};

const MapDroppableZone_droppableZone = graphql__1dbd03b7974fb700a60a;

export default MapDroppableZone;
